import React from 'react'
import styled from 'styled-components'
import { ButtonLight, ButtonOutlined } from '../../../../components/Button'
import { formatUnits } from 'ethers/lib/utils'
import { BigNumber } from 'ethers'
import { roundToDecimalPlace } from '../../../../utils/data'
import { useTransactionAdder } from '../../../../state/transactions/hooks'
import { useActiveWeb3React } from '../../../../hooks'
import { calculateGasMargin, getContract } from '../../../../utils'
import { abi as XGrailTokenABI } from '../../../../abis/x-grail-token.json'
import { XHSPX_ADDRESS } from '@hyperswap/addresses'

const ItemStyled = styled.div`
  border-radius: 12px;
  background-color: ${({ theme }) => theme.bg1}
  padding: 1.125rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: lightgrey;

  h3 {
    margin-top: 0;
    margin-bottom: 2px;
  }

  button {
    margin-top: 6px;
  }
`

const ItemValueStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`

const TagLocked = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  background: ${({ theme }) => theme.bg2};
  color: rgb(74, 252, 56);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px;
  border-radius: 12px;
`

const TagUnlocked = styled.div`
  font-size: 0.75rem;
  font-weight: 400;
  background: rgb(74, 252, 56);
  color: black;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px;
  border-radius: 12px;
`
export interface Countdown {
  days: number
  hours: number
  minutes: number
  seconds: number
  isPast: boolean
}

export function timestampToCountdown(timestamp: string): Countdown {
  const targetDate = new Date(parseInt(timestamp) * 1000)
  const currentDate = new Date()
  const isPast = targetDate < currentDate

  const diff = Math.abs(targetDate.getTime() - currentDate.getTime())

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  return { days, hours, minutes, seconds, isPast }
}

interface RedeemStakeCardInterface {
  vesting: any
  refresh: () => void
  id: number
}
const RedeemStakeCard: React.FC<RedeemStakeCardInterface> = ({ vesting, id, refresh }) => {
  const { account, chainId, library } = useActiveWeb3React()

  const [countdown, setCountdown] = React.useState<Countdown | null>(null)
  const [loadingCancel, setLoadingCancel] = React.useState<boolean>(false)

  const amountVested = vesting?.xGrailAmount ? vesting?.xGrailAmount : BigNumber.from(0)
  const amountVestedFormatted = formatUnits(amountVested)

  const amountClaimable = vesting?.grailAmount ? vesting?.grailAmount : BigNumber.from(0)
  const amountClaimableFormatted = formatUnits(amountClaimable)

  const deadline = vesting?.endTime ? vesting?.endTime.toString() : '0'
  const deadlineFormatted = timestampToCountdown(deadline)
  const isPast = deadlineFormatted.isPast

  const addTransaction = useTransactionAdder()
  const [loadingAdd, setLoadingAdd] = React.useState<boolean>(false)

  async function onClaim() {
    if (!chainId || !library || !account) return
    const xtokenContract = getContract(XHSPX_ADDRESS, XGrailTokenABI, library, account)

    const estimate = xtokenContract.estimateGas.finalizeRedeem
    const method = xtokenContract.finalizeRedeem

    const args = [id]

    setLoadingAdd(true)

    await estimate(...args, {})
      .then((estimatedGasLimit: any) => {
        return method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Finalize xHSPX Redeem`,
          })

          await response.wait()
          refresh()
          setLoadingAdd(false)
        })
      })
      .catch((error) => {
        setLoadingAdd(false)
        console.error(error)
      })
  }

  async function cancel() {
    if (!chainId || !library || !account) return
    const xtokenContract = getContract(XHSPX_ADDRESS, XGrailTokenABI, library, account)

    const estimate = xtokenContract.estimateGas.cancelRedeem
    const method = xtokenContract.cancelRedeem
    const args = [id]

    setLoadingCancel(true)

    await estimate(...args, {})
      .then((estimatedGasLimit) =>
        method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Cancel Redeem Process`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          refresh()
          setLoadingCancel(false)
        })
      )
      .catch((error) => {
        setLoadingCancel(false)
        console.error(error)
      })
  }

  React.useEffect(() => {
    setCountdown(timestampToCountdown(deadline))

    const interval = setInterval(() => {
      setCountdown(timestampToCountdown(deadline))
    }, 1000)

    return () => clearInterval(interval)
  }, [deadline])

  if (!countdown) return null
  return (
    <ItemStyled>
      <ItemValueStyled>
        <h3>Position #{id + 1}</h3>
        {isPast ? <TagUnlocked>Unlocked</TagUnlocked> : <TagLocked>Pending</TagLocked>}
      </ItemValueStyled>
      <ItemValueStyled>
        <span>Vested $xSWAP</span>
        <span style={{ color: '#4AFC38' }}>{roundToDecimalPlace(amountVestedFormatted, '4')} $xSWAP</span>
      </ItemValueStyled>
      <ItemValueStyled>
        <span>$SWAP Amount</span>
        <span style={{ color: '#4AFC38' }}>{roundToDecimalPlace(amountClaimableFormatted, '4')} $SWAP</span>
      </ItemValueStyled>
      {!isPast && (
        <ItemValueStyled>
          <span>Time left</span>
          <span
            style={{ color: '#4AFC38' }}
          >{`${countdown.days}d ${countdown.hours}h ${countdown.minutes}m ${countdown.seconds}s`}</span>
        </ItemValueStyled>
      )}
      <div style={{ display: 'flex', gap: '4px' }}>
        <ButtonLight disabled={isPast === false || loadingAdd === true} onClick={() => onClaim()}>
          {loadingAdd ? 'Claiming...' : 'Claim'}
        </ButtonLight>
        {isPast === false && (
          <ButtonOutlined disabled={loadingCancel} onClick={() => cancel()}>
            {loadingCancel ? 'Canceling...' : 'Cancel'}
          </ButtonOutlined>
        )}
      </div>
    </ItemStyled>
  )
}
export default RedeemStakeCard
